import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import classNames from "classnames";
import React, { useEffect } from "react";
import { t } from "ttag";

import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { trackEvent } from "../../../utils/analytics";
import { useHostedApplicationUrl } from "../../../utils/financing";
// eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
import { IReduxFormState as ICheckoutForm } from "../../checkout/reducers.interfaces";
import { captureFinancingModalTrigger } from "../financingModalFocus";
import { PrefillData } from "../models";
import { openApplyModal, openPreQualModal } from "../reducers";

type BtnProps = React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
>;

type ModalType = "prequal-app" | "full-app";

interface Props extends BtnProps {
    modalType: ModalType;
    prefillData?: PrefillData;
    // Props for opening/customizing the custom apply form modal
    purchasePrice?: number;
    applicationSource?: string;
    onClick?: (
        e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    ) => void;
}

const sendTrackingEvents = (modalType: ModalType): void => {
    if (modalType === "prequal-app") {
        trackEvent("Financing", "renderPreQualModalButton", undefined, {
            nonInteraction: true,
        });
    } else {
        trackEvent("Financing", "renderApplyFormModalButton", undefined, {
            nonInteraction: true,
        });
    }
};

const getPrefillData = (
    checkoutFormData: ICheckoutForm,
): PrefillData | undefined => {
    return {
        first_name: checkoutFormData.billing_first_name,
        last_name: checkoutFormData.billing_last_name,
        email: checkoutFormData.guest_email,
        address_line_1: checkoutFormData.billing_line1,
        address_line_2: checkoutFormData.billing_line2,
        city: checkoutFormData.billing_line4,
        state_code: checkoutFormData.billing_state,
        postal_code: checkoutFormData.billing_postcode
            .replace(/[^0-9]/g, "")
            .slice(0, 5),
        home_phone: checkoutFormData.billing_phone_number,
        mobile_phone: "",
        // Intentionally empty: prefilling mobile_phone from billing shadowed the
        // user-corrected home_phone in SMS verification (pickVerificationPhone()
        // prefers mobile). Leave it blank so verification falls back to home_phone.
        // See https://thelabnyc.plan.io/issues/34853
    };
};

/**
 * Hosted mode: an outbound link to Wells Fargo's hosted credit application.
 * Carries none of the in-site modal wiring.
 */
const HostedApplicationLink = (
    props: React.PropsWithChildren<Props & { hostedUrl: string }>,
): React.JSX.Element => {
    const { modalType, hostedUrl, onClick, ...btnProps } = props;

    // On first mount, track that we rendered this link
    useEffect(() => {
        trackEvent("Financing", "renderHostedApplicationLink", modalType, {
            nonInteraction: true,
        });
    }, []);

    const onHostedClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        trackEvent("Financing", "clickHostedApplicationLink", modalType);
        if (onClick) {
            onClick(e);
        }
    };

    return (
        <a
            href={hostedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames("financing-hosted-link", btnProps.className)}
            title={btnProps.title}
            id={btnProps.id}
            tabIndex={btnProps.tabIndex}
            onClick={onHostedClick}
        >
            {props.children}
            <VisuallyHidden.Root>{t`(opens in new tab)`}</VisuallyHidden.Root>
        </a>
    );
};

/**
 * Default mode: a button which opens the in-site application modal,
 * prefilled from the checkout form when available.
 */
const ModalTriggerButton = (
    props: React.PropsWithChildren<Props>,
): React.JSX.Element => {
    const {
        modalType,
        prefillData: maybePrefillData,
        purchasePrice,
        applicationSource,
        onClick,
        ...btnProps
    } = props;

    // On first mount, track that we rendered this button
    useEffect(() => {
        sendTrackingEvents(modalType);
    }, []);

    // Setup click handlers
    const dispatch = useAppDispatch();
    const checkoutFormData = useAppSelector((state) => state.checkout.form);

    const onOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        // Bubble up the event
        if (onClick) {
            onClick(e);
        }
        const prefillData =
            maybePrefillData ?? getPrefillData(checkoutFormData);
        captureFinancingModalTrigger(e.currentTarget);
        // Open the modal
        if (modalType === "prequal-app") {
            dispatch(openPreQualModal({ prefillData }));
        } else {
            dispatch(
                openApplyModal({
                    purchasePrice,
                    applicationSource,
                    prefillData,
                }),
            );
        }
    };

    return (
        <button {...btnProps} onClick={onOpen}>
            {props.children}
        </button>
    );
};

export const FinancingModalTrigger = (
    props: React.PropsWithChildren<Props>,
): React.JSX.Element => {
    // In hosted mode, link out to Wells Fargo's hosted application instead
    // of opening the in-site application modal. CSRs are the exception: they
    // submit the full application for a customer over the phone and can't
    // follow an outbound link, so they keep the in-site form. Pre-qualification
    // links out for everyone, CSRs included.
    //
    // is_csr comes from Redux (populated by loadCurrentUser during CSR toolbar
    // init) rather than a page setting: page settings are baked into CDN-cached
    // HTML that does not vary on Cookie, so a per-user value there would leak
    // across sessions. This is a UI affordance, not an authorization boundary —
    // MerchantAccount.user_group scopes the APPLY-capable merchant credentials
    // to the CSR group, so a non-CSR posting to the apply endpoint still fails
    // merchant resolution.
    const hostedUrl = useHostedApplicationUrl();
    const isCSR = useAppSelector((state) => state.common.user?.is_csr ?? false);
    if (hostedUrl && !(isCSR && props.modalType === "full-app")) {
        return <HostedApplicationLink {...props} hostedUrl={hostedUrl} />;
    }
    return <ModalTriggerButton {...props} />;
};
